"use client";
import React, { ReactNode } from "react";
import { HTMLElementType } from "@/types/baseTypes";
import { useScrollAnimation, AnimationType } from "@/hooks/useScrollAnimation";

/* jscpd:ignore-start */
interface ScrollAnimationProps {
  children: ReactNode;
  className?: string;
  as?: HTMLElementType;
  animation?: AnimationType;
  threshold?: number;
  triggerOnce?: boolean;
  delay?: number;
  /* jscpd:ignore-end */
  duration?: number;
  disabled?: boolean;
  disableThreshold?: boolean;
  observeWrapper?: boolean;
  viewport?: {
    amount?: number;
    margin?: string;
  };
}

/**
 * ScrollAnimation Component
 *
 * A WCAG 2.1-compliant scroll animation component that uses IntersectionObserver
 * to trigger CSS animations when elements enter the viewport.
 *
 * @example
 * ```tsx
 * <ScrollAnimation animation="fade-in-up" delay={200}>
 *   <YourContent />
 * </ScrollAnimation>
 * ```
 */
/* jscpd:ignore-start */
export const ScrollAnimation: React.FC<ScrollAnimationProps> = ({
  children,
  className = "",
  as: Component = "div",
  animation = "fade-in-up",
  threshold = 0.3,
  triggerOnce = true,
  delay,
  /* jscpd:ignore-end */
  duration,
  disabled = false,
  disableThreshold = false,
  observeWrapper = false,
  viewport,
}) => {
  const { ref, className: animationClassName } = useScrollAnimation({
    animation,
    threshold,
    triggerOnce,
    delay,
    duration,
    disabled,
    disableThreshold,
    viewport,
  });

  if (observeWrapper) {
    return (
      <div ref={ref}>
        <Component className={`${animationClassName} ${className}`.trim()}>
          {children}
        </Component>
      </div>
    );
  }

  return (
    <Component
      ref={ref}
      className={`${animationClassName} ${className}`.trim()}
    >
      {children}
    </Component>
  );
};

// Specialized component variants for common animations

/**
 * FadeIn - Simple opacity fade animation
 */
export const FadeIn: React.FC<Omit<ScrollAnimationProps, "animation">> = (
  props,
) => <ScrollAnimation {...props} animation="fade-in" />;

/**
 * FadeInUp - Fade in with slide from bottom (most common)
 */
export const FadeInUp: React.FC<Omit<ScrollAnimationProps, "animation">> = (
  props,
) => <ScrollAnimation {...props} animation="fade-in-up" />;

/**
 * FadeInDown - Fade in with slide from top
 */
export const FadeInDown: React.FC<Omit<ScrollAnimationProps, "animation">> = (
  props,
) => <ScrollAnimation {...props} animation="fade-in-down" />;

/**
 * SlideLeft - Fade in with slide from left
 */
export const SlideLeft: React.FC<Omit<ScrollAnimationProps, "animation">> = (
  props,
) => <ScrollAnimation {...props} animation="slide-left" />;

/**
 * SlideRight - Fade in with slide from right
 */
export const SlideRight: React.FC<Omit<ScrollAnimationProps, "animation">> = (
  props,
) => <ScrollAnimation {...props} animation="slide-right" />;

/**
 * ScaleUp - Fade in with scale from 90%
 */
export const ScaleUp: React.FC<Omit<ScrollAnimationProps, "animation">> = (
  props,
) => <ScrollAnimation {...props} animation="scale-up" />;

/**
 * RotateIn - Fade in with rotate and scale effect
 */
export const RotateIn: React.FC<Omit<ScrollAnimationProps, "animation">> = (
  props,
) => <ScrollAnimation {...props} animation="rotate-in" />;

/**
 * FadeInUpSmall - Fade in with small slide from bottom (subtle)
 */
export const FadeInUpSmall: React.FC<
  Omit<ScrollAnimationProps, "animation">
> = (props) => <ScrollAnimation {...props} animation="fade-in-up-small" />;

/**
 * FadeInUpLarge - Fade in with large slide from bottom (dramatic)
 */
export const FadeInUpLarge: React.FC<
  Omit<ScrollAnimationProps, "animation">
> = (props) => <ScrollAnimation {...props} animation="fade-in-up-large" />;

/**
 * FadeInDownLarge - Fade in with large slide from top (dramatic)
 */
export const FadeInDownLarge: React.FC<
  Omit<ScrollAnimationProps, "animation">
> = (props) => <ScrollAnimation {...props} animation="fade-in-down-large" />;

/**
 * SlideLeftLarge - Fade in with large slide from left (dramatic)
 */
export const SlideLeftLarge: React.FC<
  Omit<ScrollAnimationProps, "animation">
> = (props) => <ScrollAnimation {...props} animation="slide-left-large" />;

/**
 * SlideRightLarge - Fade in with large slide from right (dramatic)
 */
export const SlideRightLarge: React.FC<
  Omit<ScrollAnimationProps, "animation">
> = (props) => <ScrollAnimation {...props} animation="slide-right-large" />;

/**
 * SlideUpNoOpacity - Slide up without opacity change
 */
export const SlideUpNoOpacity: React.FC<
  Omit<ScrollAnimationProps, "animation">
> = (props) => <ScrollAnimation {...props} animation="slide-up-no-opacity" />;

/**
 * ClipHorizontal - Horizontal clip-path reveal animation
 */
export const ClipHorizontal: React.FC<
  Omit<ScrollAnimationProps, "animation">
> = (props) => <ScrollAnimation {...props} animation="clip-horizontal" />;

/**
 * HeightExpand - Height expansion animation
 */
export const HeightExpand: React.FC<Omit<ScrollAnimationProps, "animation">> = (
  props,
) => <ScrollAnimation {...props} animation="height-expand" />;

/**
 * ClipCircleReveal - Circular clip-path reveal from top
 */
export const ClipCircleReveal: React.FC<
  Omit<ScrollAnimationProps, "animation">
> = (props) => <ScrollAnimation {...props} animation="clip-circle-reveal" />;
